import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { errorMessage, fetchInvestScoreboard, type InvestScorePoint } from './api';

const signedPercent = new Intl.NumberFormat('it-IT', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  signDisplay: 'always',
});
const shortDate = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short' });
const money = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

function score(value: number) {
  return Number.isFinite(value) ? `${signedPercent.format(value)}%` : 'n/d';
}

function ScoreboardChart({ series, positive }: { series: InvestScorePoint[]; positive: boolean }) {
  const chart = useMemo(() => {
    const rows = [...series]
      .filter((row) => Number.isFinite(row.noi) && Number.isFinite(row.mercato))
      .sort((a, b) => a.day.localeCompare(b.day));
    const values = rows.flatMap((row) => [row.noi, row.mercato]);
    if (rows.length < 2 || values.length === 0) return null;
    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    const padding = Math.max((rawMax - rawMin) * .12, Math.abs(rawMax) * .005, 1);
    const min = rawMin - padding;
    const max = rawMax + padding;
    const left = 12, right = 388, top = 13, bottom = 143;
    const position = (value: number, index: number) => ({
      x: left + index / Math.max(rows.length - 1, 1) * (right - left),
      y: bottom - (value - min) / Math.max(max - min, .000001) * (bottom - top),
    });
    const pathFor = (key: 'noi' | 'mercato') => rows.map((row, index) => {
      const point = position(row[key], index);
      return `${index === 0 ? 'M' : 'L'}${point.x.toFixed(2)},${point.y.toFixed(2)}`;
    }).join(' ');
    return {
      rows,
      noiPath: pathFor('noi'),
      mercatoPath: pathFor('mercato'),
      noiLast: position(rows.at(-1)!.noi, rows.length - 1),
      mercatoLast: position(rows.at(-1)!.mercato, rows.length - 1),
    };
  }, [series]);

  if (!chart) return <div className="invest-scoreboard-empty"><strong>Storico in costruzione</strong><p>Servono almeno due rilevazioni per confrontare le due curve.</p></div>;

  return <figure className={`invest-scoreboard-chart ${positive ? 'is-ahead' : 'is-behind'}`}>
    <svg viewBox="0 0 400 156" preserveAspectRatio="none" role="img" aria-label="Andamento comparato del laboratorio e del mercato">
      {[35, 71, 107, 143].map((y) => <line key={y} x1="12" x2="388" y1={y} y2={y} className="invest-scoreboard-grid"/>)}
      <path d={chart.mercatoPath} className="invest-scoreboard-market-line"/>
      <path d={chart.noiPath} className="invest-scoreboard-us-line"/>
      <circle cx={chart.mercatoLast.x} cy={chart.mercatoLast.y} r="3.5" className="invest-scoreboard-market-dot"/>
      <circle cx={chart.noiLast.x} cy={chart.noiLast.y} r="4" className="invest-scoreboard-us-dot"/>
    </svg>
    <figcaption><span>{shortDate.format(new Date(`${chart.rows[0].day}T12:00:00`))}</span><span>{shortDate.format(new Date(`${chart.rows.at(-1)!.day}T12:00:00`))}</span></figcaption>
  </figure>;
}

export default function InvestScoreboard({ expert = false }: { expert?: boolean }) {
  const [days, setDays] = useState<30 | 90 | 365>(90);
  const scoreboard = useQuery({
    queryKey: ['invest-scoreboard', days],
    queryFn: () => fetchInvestScoreboard(days),
  });

  if (scoreboard.isPending) return <section className="invest-scoreboard is-loading" aria-label="Noi vs Mercato" aria-busy="true"><div/><div/><div/></section>;
  if (scoreboard.isError) return <section className="invest-scoreboard is-error" aria-labelledby="invest-scoreboard-title"><header><div><h2 id="invest-scoreboard-title">I tuoi bot contro il mercato</h2></div></header><p>{errorMessage(scoreboard.error)}</p><button type="button" className="btn-secondary" onClick={() => scoreboard.refetch()}>Riprova</button></section>;

  const result = scoreboard.data;
  const state = result.stato ?? ((result.mosse ?? 0) === 0 ? 'in_attesa' : result.deltaPct >= 0 ? 'avanti' : 'indietro');
  const positive = state === 'avanti';
  const waiting = state === 'in_attesa';
  const latest = [...result.serie].sort((a, b) => a.day.localeCompare(b.day)).at(-1);
  const accountValue = Number.isFinite(result.valoreConto) ? result.valoreConto : latest?.noi;
  const cash = Number.isFinite(result.cassa) ? result.cassa : accountValue;
  const marketValue = latest?.mercato;
  const title = waiting ? 'I bot non hanno ancora giocato' : positive ? 'Stiamo vincendo' : 'Il mercato è avanti';
  const subtitle = waiting
    ? `Il conto è tutto in contanti. Il mercato intanto ha fatto ${score(result.mercatoReturnPct)}.`
    : positive
      ? `Dopo ${result.mosse} ${result.mosse === 1 ? 'mossa' : 'mosse'}, i bot sono davanti al mercato.`
      : `Dopo ${result.mosse} ${result.mosse === 1 ? 'mossa' : 'mosse'}, il mercato resta davanti ai bot.`;
  const comparison = `${waiting ? 'Il tuo conto è fermo' : 'Il tuo conto vale'} ${accountValue == null ? 'n/d' : money.format(accountValue)}; comprando e tenendo avresti ${marketValue == null ? 'n/d' : money.format(marketValue)}.`;
  return <section className={`invest-scoreboard is-${state}`} aria-labelledby="invest-scoreboard-title">
    <header><div className="invest-scoreboard-verdict"><span className="invest-scoreboard-status" aria-hidden="true">{waiting ? '…' : <svg viewBox="0 0 20 20"><path d={positive ? 'm5 10 3 3 7-7' : 'm5 7 3-3 7 7'}/></svg>}</span><div><h2 id="invest-scoreboard-title">{title}</h2><p>{subtitle}</p></div></div></header>
    <div className="invest-scoreboard-metrics">
      <article className="is-account"><span>Il tuo conto</span><strong>{accountValue == null ? 'n/d' : money.format(accountValue)}</strong></article>
      <article><span>Di cui in contanti</span><strong>{cash == null ? 'n/d' : money.format(cash)}</strong></article>
      <article className="is-simulation"><span>Se avessi comprato e tenuto</span><strong>{marketValue == null ? 'n/d' : money.format(marketValue)}</strong><small>simulazione: non sono soldi tuoi</small></article>
    </div>
    <div className="invest-scoreboard-visual"><ScoreboardChart series={result.serie} positive={state !== 'indietro'}/><footer><div className="invest-scoreboard-legend"><span className="is-us">Il tuo conto</span><span className="is-market">Compra e mantieni</span></div><div className="invest-scoreboard-window" role="group" aria-label="Periodo del confronto">{([30, 90, 365] as const).map((value) => <button type="button" key={value} className={days === value ? 'is-active' : ''} aria-pressed={days === value} onClick={() => setDays(value)}>{value === 30 ? '1M' : value === 90 ? '3M' : '1A'}</button>)}</div></footer></div>
    <p className="invest-scoreboard-comparison">{comparison}</p>
    {expert && <details className="invest-scoreboard-expert"><summary>Numeri completi</summary><dl><div><dt>Risultato dei bot</dt><dd>{score(result.noiReturnPct)}</dd></div><div><dt>Risultato del mercato</dt><dd>{score(result.mercatoReturnPct)}</dd></div><div><dt>Differenza</dt><dd>{score(result.deltaPct)}</dd></div></dl></details>}
  </section>;
}
